/** For grouping trails that share one spawn (same click / cohort). */
export interface HasSpawnGroupId {
    id: string;
    spawnGroupId?: string;
}

/**
 * Cluster adjacent items with the same spawn group. Preserves global order; use one `<g>` per
 * cluster so paint order stays correct when different spawns interleave.
 */
export function clusterConsecutiveBySpawnGroupId<T extends HasSpawnGroupId>(
    items: T[]
): { spawnGroupId: string; items: T[] }[] {
    const out: { spawnGroupId: string; items: T[] }[] = [];
    for (const item of items) {
        const gid = item.spawnGroupId ?? item.id;
        const last = out[out.length - 1];
        if (last && last.spawnGroupId === gid) {
            last.items.push(item);
        } else {
            out.push({ spawnGroupId: gid, items: [item] });
        }
    }
    return out;
}

/** Parsed viewBox: `minX minY width height` per SVG spec. */
export interface ViewBoxBounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

export function parseViewBoxBounds(viewBoxAttr: string): ViewBoxBounds | null {
    const parts = viewBoxAttr.trim().split(/\s+/).map(Number);
    if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) {
        return null;
    }
    const [minX, minY, w, h] = parts;
    if (w <= 0 || h <= 0) return null;
    return {
        minX,
        minY,
        maxX: minX + w,
        maxY: minY + h,
    };
}

/** True if the point is on or outside the viewBox rectangle (agent should be destroyed). */
export function isOutsideOrOnEdge(
    x: number,
    y: number,
    b: ViewBoxBounds
): boolean {
    return x <= b.minX || x >= b.maxX || y <= b.minY || y >= b.maxY;
}

/** Uniform random point in a disk centered at `(cx, cy)` with given `radius`. */
export function randomPointInDisk(
    cx: number,
    cy: number,
    radius: number
): { x: number; y: number } {
    const t = Math.random() * Math.PI * 2;
    const r = radius * Math.sqrt(Math.random());
    return { x: cx + r * Math.cos(t), y: cy + r * Math.sin(t) };
}

/** Keep the point strictly inside the viewBox (small margin from edges). */
export function clampPointInsideBounds(
    x: number,
    y: number,
    b: ViewBoxBounds,
    margin = 0.02
): { x: number; y: number } {
    return {
        x: Math.min(b.maxX - margin, Math.max(b.minX + margin, x)),
        y: Math.min(b.maxY - margin, Math.max(b.minY + margin, y)),
    };
}

export function pointsToPathD(points: ReadonlyArray<{ x: number; y: number }>): string {
    if (points.length === 0) return "";
    const [p0, ...rest] = points;
    let d = `M ${p0.x} ${p0.y}`;
    for (const p of rest) {
        d += ` L ${p.x} ${p.y}`;
    }
    return d;
}

/**
 * Catmull-Rom–style smooth curve through points, as cubic Bézier segments.
 * Falls back to polyline for 0–2 points.
 */
export function pointsToSmoothPathD(
    points: ReadonlyArray<{ x: number; y: number }>
): string {
    const n = points.length;
    if (n === 0) return "";
    if (n === 1) return `M ${points[0].x} ${points[0].y}`;
    if (n === 2) {
        return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
    }
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < n - 1; i++) {
        const p0 = i === 0 ? points[0] : points[i - 1];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = i + 2 < n ? points[i + 2] : points[i + 1];
        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p2.x} ${p2.y}`;
    }
    return d;
}

export function pointsToPathDWithStyle(
    points: ReadonlyArray<{ x: number; y: number }>,
    smooth: boolean
): string {
    return smooth ? pointsToSmoothPathD(points) : pointsToPathD(points);
}

export function clampSpeed(vx: number, vy: number, maxSpeed: number): { vx: number; vy: number } {
    const s = Math.hypot(vx, vy);
    if (s <= maxSpeed || s === 0) return { vx, vy };
    const k = maxSpeed / s;
    return { vx: vx * k, vy: vy * k };
}

/**
 * Lock agent motion to axis-aligned (`deg90`: up/down/left/right) or 8-way
 * (`deg45`: + diagonals) directions; `none` leaves the velocity unchanged.
 */
export type DirectionLockMode = "none" | "deg45" | "deg90";

/**
 * Snap a velocity to the nearest allowed direction for the lock mode, preserving
 * speed. A zero or near-zero velocity is returned unchanged.
 */
export function snapVelocityToDirectionLock(
    vx: number,
    vy: number,
    mode: DirectionLockMode,
): { vx: number; vy: number } {
    if (mode === "none") return { vx, vy };
    const speed = Math.hypot(vx, vy);
    if (speed < 1e-9) return { vx, vy };
    const stepRad = (mode === "deg90" ? 90 : 45) * (Math.PI / 180);
    const snapped = Math.round(Math.atan2(vy, vx) / stepRad) * stepRad;
    return { vx: Math.cos(snapped) * speed, vy: Math.sin(snapped) * speed };
}

/** Random unit direction times `speed`. */
export function randomVelocity(
    speed: number,
    random: () => number = Math.random
): { vx: number; vy: number } {
    const t = random() * Math.PI * 2;
    return { vx: Math.cos(t) * speed, vy: Math.sin(t) * speed };
}

/** Add random impulse scaled by `magnitude`, then clamp to maxSpeed. */
export function applyVelocityKick(
    vx: number,
    vy: number,
    magnitude: number,
    maxSpeed: number,
    random: () => number = Math.random
): { vx: number; vy: number } {
    const ax = (random() * 2 - 1) * magnitude;
    const ay = (random() * 2 - 1) * magnitude;
    return clampSpeed(vx + ax, vy + ay, maxSpeed);
}

/** Uniform jitter in ±randomnessSec around baseSec; clamped to a small minimum. */
export function sampleKickDelay(
    baseSec: number,
    randomnessSec: number,
    random: () => number = Math.random
): number {
    const jitter = (random() * 2 - 1) * randomnessSec;
    return Math.max(0.05, baseSec + jitter);
}

/**
 * Per-agent countdown for spawning a child at the agent position. When
 * `baseIntervalSec` <= 0, spawning is off and the timer is cleared.
 */
export function advancePeriodicSpawnTimer(
    prevSeconds: number | undefined,
    dt: number,
    baseIntervalSec: number,
    randomnessSec: number,
    random: () => number = Math.random,
): { nextSeconds: number | undefined; shouldSpawn: boolean } {
    if (baseIntervalSec <= 0) {
        return { nextSeconds: undefined, shouldSpawn: false };
    }
    let t =
        prevSeconds ?? sampleKickDelay(baseIntervalSec, randomnessSec, random);
    t -= dt;
    if (t <= 0) {
        return {
            nextSeconds: sampleKickDelay(
                baseIntervalSec,
                randomnessSec,
                random,
            ),
            shouldSpawn: true,
        };
    }
    return { nextSeconds: t, shouldSpawn: false };
}

/** Random delta in ±step, clamped to [minW, maxW]. */
export function applyStrokeWidthKick(
    current: number,
    step: number,
    minW: number,
    maxW: number,
    random: () => number = Math.random
): number {
    const delta = (random() * 2 - 1) * step;
    return Math.min(maxW, Math.max(minW, current + delta));
}

export function trailUsesVaryingWidth(
    strokeWidths: ReadonlyArray<number> | undefined,
    pointCount: number
): boolean {
    if (!strokeWidths || strokeWidths.length !== pointCount || pointCount < 2) {
        return false;
    }
    return new Set(strokeWidths).size > 1;
}

export function meanStrokeWidth(widths: ReadonlyArray<number>): number {
    if (widths.length === 0) return 0;
    let s = 0;
    for (const w of widths) s += w;
    return s / widths.length;
}

export const MIN_TRAIL_LENGTH_CAP = 50;
export const MAX_TRAIL_LENGTH_CAP = 50000;

/**
 * Child inherits the parent's remaining trail budget (px). Clamped to
 * `[1, MAX_TRAIL_LENGTH_CAP]` so tiny remainders still draw a short trail.
 */
export function clampTrailCapFromRemaining(remainingPx: number): number {
    if (!Number.isFinite(remainingPx)) return MIN_TRAIL_LENGTH_CAP;
    return Math.min(
        MAX_TRAIL_LENGTH_CAP,
        Math.max(1, remainingPx),
    );
}

/** How spawned child agents get their max trail length cap. */
export type ChildTrailCapMode = "none" | "parentRemaining" | "fixed";

/**
 * Max trail length for a child agent from settings.
 * - `none`: same distribution as a normal spawn using global max trail length (when child randomness is 0, uses global max trail randomness).
 * - `parentRemaining`: parent's remaining arc budget after the spawn step, then child randomness.
 * - `fixed`: user base length, then child randomness.
 */
export function computeChildTrailLengthCap(options: {
    mode: ChildTrailCapMode;
    maxTrailLength: number;
    maxTrailLengthRandomness: number;
    parentRemaining?: number;
    fixedCap: number;
    childTrailCapRandomness: number;
}): number {
    const cr = Math.min(1, Math.max(0, options.childTrailCapRandomness));
    switch (options.mode) {
        case "none": {
            const r =
                options.childTrailCapRandomness > 0
                    ? options.childTrailCapRandomness
                    : options.maxTrailLengthRandomness;
            return sampleTrailLengthCap(options.maxTrailLength, r);
        }
        case "parentRemaining": {
            const base = clampTrailCapFromRemaining(
                options.parentRemaining ?? 0,
            );
            return sampleTrailLengthCap(base, cr);
        }
        case "fixed": {
            const base = Math.min(
                MAX_TRAIL_LENGTH_CAP,
                Math.max(MIN_TRAIL_LENGTH_CAP, options.fixedCap),
            );
            return sampleTrailLengthCap(base, cr);
        }
    }
}

/** Per-agent cap: `base * (1 + u * randomness)` with u ~ uniform[-1, 1], then clamped. */
export function sampleTrailLengthCap(
    base: number,
    randomness: number,
    random: () => number = Math.random,
): number {
    const b = Math.min(
        MAX_TRAIL_LENGTH_CAP,
        Math.max(MIN_TRAIL_LENGTH_CAP, base),
    );
    const r = Math.min(1, Math.max(0, randomness));
    if (r <= 0) return b;
    const u = random() * 2 - 1;
    return Math.min(
        MAX_TRAIL_LENGTH_CAP,
        Math.max(MIN_TRAIL_LENGTH_CAP, b * (1 + u * r)),
    );
}

function smoothstep01(t: number): number {
    const x = Math.min(1, Math.max(0, t));
    return x * x * (3 - 2 * x);
}

/** Cumulative arc length from the trail start along the polyline (one entry per vertex). */
export function polylineVertexArcLengths(
    points: ReadonlyArray<{ x: number; y: number }>,
): number[] {
    const n = points.length;
    const acc: number[] = new Array(n);
    acc[0] = 0;
    for (let i = 1; i < n; i++) {
        const dx = points[i]!.x - points[i - 1]!.x;
        const dy = points[i]!.y - points[i - 1]!.y;
        acc[i] = acc[i - 1]! + Math.hypot(dx, dy);
    }
    return acc;
}

/**
 * Per-vertex width scale from 0 at the trail tips to 1 in the interior,
 * over the given arc lengths (SVG user units). Smoothstep on distance to each tip.
 */
export function applyEndpointStrokeTaper(
    points: ReadonlyArray<{ x: number; y: number }>,
    baseWidths: ReadonlyArray<number>,
    taperStartLength: number,
    taperEndLength: number,
): number[] {
    const n = points.length;
    if (n < 2 || baseWidths.length !== n) {
        return baseWidths.length ? Array.from(baseWidths) : [];
    }
    const arc = polylineVertexArcLengths(points);
    const total = arc[n - 1]!;
    const ts = Math.max(0, taperStartLength);
    const te = Math.max(0, taperEndLength);
    const out = new Array<number>(n);
    for (let i = 0; i < n; i++) {
        const a = arc[i]!;
        let f = 1;
        if (ts > 0) {
            f *= smoothstep01(a / ts);
        }
        if (te > 0 && total > 0) {
            f *= smoothstep01((total - a) / te);
        }
        out[i] = Math.max(0, baseWidths[i]! * f);
    }
    return out;
}

/**
 * Widths for rendering/export: optional per-vertex `strokeWidths`, else uniform `fallbackStrokeWidth`,
 * then endpoint taper in user units (0 = no taper on that end).
 */
export function effectiveTrailStrokeWidths(
    points: ReadonlyArray<{ x: number; y: number }>,
    strokeWidths: ReadonlyArray<number> | undefined,
    fallbackStrokeWidth: number,
    taperStartLength: number,
    taperEndLength: number,
): ReadonlyArray<number> {
    const n = points.length;
    if (n < 2) return [];
    const noTaper = taperStartLength <= 0 && taperEndLength <= 0;
    if (noTaper) {
        if (strokeWidths && strokeWidths.length === n) return strokeWidths;
        return Array.from({ length: n }, () => fallbackStrokeWidth);
    }
    const base =
        strokeWidths && strokeWidths.length === n
            ? Array.from(strokeWidths)
            : Array.from({ length: n }, () => fallbackStrokeWidth);
    return applyEndpointStrokeTaper(
        points,
        base,
        taperStartLength,
        taperEndLength,
    );
}

function perpLeftUnit(dx: number, dy: number): { x: number; y: number } {
    const len = Math.hypot(dx, dy);
    if (len < 1e-12) return { x: 0, y: 0 };
    return { x: -dy / len, y: dx / len };
}

function lineLineIntersection(
    ax: number,
    ay: number,
    bx: number,
    by: number,
    cx: number,
    cy: number,
    dx: number,
    dy: number
): { x: number; y: number } | null {
    const rX = bx - ax;
    const rY = by - ay;
    const sX = dx - cx;
    const sY = dy - cy;
    const denom = rX * sY - rY * sX;
    if (Math.abs(denom) < 1e-12) return null;
    const t = ((cx - ax) * sY - (cy - ay) * sX) / denom;
    return { x: ax + t * rX, y: ay + t * rY };
}

function computePolylineStrokeSideChains(
    points: ReadonlyArray<{ x: number; y: number }>,
    halfWidths: ReadonlyArray<number>,
    miterLimit: number,
): { left: { x: number; y: number }[]; right: { x: number; y: number }[] } | null {
    const n = points.length;
    if (n < 2 || halfWidths.length !== n) return null;

    const left: { x: number; y: number }[] = [];
    const right: { x: number; y: number }[] = [];

    {
        const dx = points[1].x - points[0].x;
        const dy = points[1].y - points[0].y;
        const nl = perpLeftUnit(dx, dy);
        const hw = halfWidths[0];
        left.push({
            x: points[0].x + nl.x * hw,
            y: points[0].y + nl.y * hw,
        });
        right.push({
            x: points[0].x - nl.x * hw,
            y: points[0].y - nl.y * hw,
        });
    }

    for (let i = 1; i <= n - 2; i++) {
        const pA = points[i - 1]!;
        const pB = points[i]!;
        const pC = points[i + 1]!;
        const dx1 = pB.x - pA.x;
        const dy1 = pB.y - pA.y;
        const dx2 = pC.x - pB.x;
        const dy2 = pC.y - pB.y;
        const n1 = perpLeftUnit(dx1, dy1);
        const n2 = perpLeftUnit(dx2, dy2);
        const hwA = halfWidths[i - 1]!;
        const hwB = halfWidths[i]!;
        const hwC = halfWidths[i + 1]!;

        const L1a = { x: pA.x + n1.x * hwA, y: pA.y + n1.y * hwA };
        const L1b = { x: pB.x + n1.x * hwB, y: pB.y + n1.y * hwB };
        const L2a = { x: pB.x + n2.x * hwB, y: pB.y + n2.y * hwB };
        const L2b = { x: pC.x + n2.x * hwC, y: pC.y + n2.y * hwC };

        const hit = lineLineIntersection(
            L1a.x,
            L1a.y,
            L1b.x,
            L1b.y,
            L2a.x,
            L2a.y,
            L2b.x,
            L2b.y
        );
        let Lm: { x: number; y: number };
        if (hit) {
            const d = Math.hypot(hit.x - pB.x, hit.y - pB.y);
            if (d > miterLimit * hwB) {
                Lm = { x: (L1b.x + L2a.x) / 2, y: (L1b.y + L2a.y) / 2 };
            } else {
                Lm = hit;
            }
        } else {
            Lm = { x: (L1b.x + L2a.x) / 2, y: (L1b.y + L2a.y) / 2 };
        }
        left.push(Lm);

        const R1a = { x: pA.x - n1.x * hwA, y: pA.y - n1.y * hwA };
        const R1b = { x: pB.x - n1.x * hwB, y: pB.y - n1.y * hwB };
        const R2a = { x: pB.x - n2.x * hwB, y: pB.y - n2.y * hwB };
        const R2b = { x: pC.x - n2.x * hwC, y: pC.y - n2.y * hwC };
        const hitR = lineLineIntersection(
            R1a.x,
            R1a.y,
            R1b.x,
            R1b.y,
            R2a.x,
            R2a.y,
            R2b.x,
            R2b.y
        );
        let Rm: { x: number; y: number };
        if (hitR) {
            const d = Math.hypot(hitR.x - pB.x, hitR.y - pB.y);
            if (d > miterLimit * hwB) {
                Rm = { x: (R1b.x + R2a.x) / 2, y: (R1b.y + R2a.y) / 2 };
            } else {
                Rm = hitR;
            }
        } else {
            Rm = { x: (R1b.x + R2a.x) / 2, y: (R1b.y + R2a.y) / 2 };
        }
        right.push(Rm);
    }

    {
        const dx = points[n - 1]!.x - points[n - 2]!.x;
        const dy = points[n - 1]!.y - points[n - 2]!.y;
        const nl = perpLeftUnit(dx, dy);
        const hw = halfWidths[n - 1]!;
        left.push({
            x: points[n - 1]!.x + nl.x * hw,
            y: points[n - 1]!.y + nl.y * hw,
        });
        right.push({
            x: points[n - 1]!.x - nl.x * hw,
            y: points[n - 1]!.y - nl.y * hw,
        });
    }

    return { left, right };
}

/** Interior samples on the semicircle centered at `cx,cy` from `pStart` around to the antipode, choosing the half that passes through `pVia` (endpoints excluded). */
function semicircleArcInteriorPoints(
    cx: number,
    cy: number,
    pStart: { x: number; y: number },
    pVia: { x: number; y: number },
    segments: number,
): { x: number; y: number }[] {
    const r = Math.hypot(pStart.x - cx, pStart.y - cy);
    if (r < 1e-12) return [];
    const aStart = Math.atan2(pStart.y - cy, pStart.x - cx);
    const aVia = Math.atan2(pVia.y - cy, pVia.x - cx);
    const twoPi = Math.PI * 2;
    const aEndCCW = aStart + Math.PI;
    const norm = (a: number) => ((a % twoPi) + twoPi) % twoPi;
    const ccwDist = (from: number, to: number) =>
        (norm(to) - norm(from) + twoPi) % twoPi;
    const dVia = ccwDist(aStart, aVia);
    const dEnd = ccwDist(aStart, aEndCCW);
    const useCCW = dVia <= dEnd + 1e-9;
    const aEnd = useCCW ? aEndCCW : aStart - Math.PI;
    const steps = Math.max(4, Math.round(segments));
    const out: { x: number; y: number }[] = [];
    for (let i = 1; i < steps; i++) {
        const t = i / steps;
        const a = aStart + t * (aEnd - aStart);
        out.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
    }
    return out;
}

/**
 * Closed ring approximating an expanded stroke with **round line caps** (SVG
 * `stroke-linecap="round"`), same offset side geometry as
 * `polylineStrokeOutlineToPathD` (miter joins with bevel fallback).
 * Use for boolean ops so unions match on-canvas stroked trails more closely
 * (Illustrator-style expanded stroke + Unite).
 */
export function polylineRoundCapStrokeOutlineToRing(
    points: ReadonlyArray<{ x: number; y: number }>,
    halfWidths: ReadonlyArray<number>,
    miterLimit = 4,
    capSegments = 12,
): [number, number][] | null {
    const chains = computePolylineStrokeSideChains(
        points,
        halfWidths,
        miterLimit,
    );
    if (!chains) return null;
    const { left, right } = chains;
    const n = points.length;
    const p0 = points[0]!;
    const p1 = points[1]!;
    const pLast = points[n - 1]!;
    const pPrev = points[n - 2]!;

    const len0 = Math.hypot(p1.x - p0.x, p1.y - p0.y);
    const lenE = Math.hypot(pLast.x - pPrev.x, pLast.y - pPrev.y);
    if (len0 < 1e-12 || lenE < 1e-12) return null;

    const t0x = (p1.x - p0.x) / len0;
    const t0y = (p1.y - p0.y) / len0;
    const tEx = (pLast.x - pPrev.x) / lenE;
    const tEy = (pLast.y - pPrev.y) / lenE;

    const hw0 = halfWidths[0]!;
    const hwE = halfWidths[n - 1]!;
    const tipStart = { x: p0.x - t0x * hw0, y: p0.y - t0y * hw0 };
    const tipEnd = { x: pLast.x + tEx * hwE, y: pLast.y + tEy * hwE };

    const endCap = semicircleArcInteriorPoints(
        pLast.x,
        pLast.y,
        left[left.length - 1]!,
        tipEnd,
        capSegments,
    );
    const startCap = semicircleArcInteriorPoints(
        p0.x,
        p0.y,
        right[0]!,
        tipStart,
        capSegments,
    );

    const ring: [number, number][] = [];
    for (const p of left) ring.push([p.x, p.y]);
    for (const p of endCap) ring.push([p.x, p.y]);
    for (let i = right.length - 1; i >= 0; i--) {
        ring.push([right[i]!.x, right[i]!.y]);
    }
    for (const p of startCap) ring.push([p.x, p.y]);
    if (ring.length < 3) return null;
    return ring;
}

/**
 * Closed ring for the filled “variable-width stroke” ribbon (same geometry as
 * `polylineStrokeOutlineToPathD`): offset left/right chains, closed with straight
 * end segments. Use when union input should match filled trail rendering.
 */
export function polylineRibbonOutlineToRing(
    points: ReadonlyArray<{ x: number; y: number }>,
    halfWidths: ReadonlyArray<number>,
    miterLimit = 4,
): [number, number][] | null {
    const chains = computePolylineStrokeSideChains(
        points,
        halfWidths,
        miterLimit,
    );
    if (!chains) return null;
    const { left, right } = chains;
    const ring: [number, number][] = [];
    for (const p of left) ring.push([p.x, p.y]);
    for (let i = right.length - 1; i >= 0; i--) {
        ring.push([right[i]!.x, right[i]!.y]);
    }
    return ring.length >= 3 ? ring : null;
}

/**
 * Closed path `d` for a filled region matching a variable-width stroke along a polyline
 * (miter joins with bevel fallback when the miter is too long).
 * `halfWidths[i]` is half the stroke width at each vertex.
 */
export function polylineStrokeOutlineToPathD(
    points: ReadonlyArray<{ x: number; y: number }>,
    halfWidths: ReadonlyArray<number>,
    miterLimit = 4
): string | null {
    const ring = polylineRibbonOutlineToRing(points, halfWidths, miterLimit);
    if (!ring || ring.length < 3) return null;
    let d = `M ${ring[0]![0]} ${ring[0]![1]}`;
    for (let i = 1; i < ring.length; i++) {
        d += ` L ${ring[i]![0]} ${ring[i]![1]}`;
    }
    d += " Z";
    return d;
}

/**
 * Offsets the nominal point using sin/cos in the velocity frame: perpendicular × sin(phase) +
 * tangent × cos(phase), so the offset rotates in the plane as arc length grows (spiral ribbon),
 * with optional amplitude growth along the path.
 */
export function applySpiralTrailOffset(
    nx: number,
    ny: number,
    vx: number,
    vy: number,
    dt: number,
    prevArcLength: number,
    amplitude: number,
    wavelength: number,
    growthPer1k: number
): { x: number; y: number; arcLength: number } {
    const ds = Math.hypot(vx * dt, vy * dt);
    const arcLength = prevArcLength + ds;
    const speed = Math.hypot(vx, vy);
    if (amplitude <= 0 || wavelength <= 1e-6 || speed < 1e-8) {
        return { x: nx, y: ny, arcLength };
    }
    const tx = vx / speed;
    const ty = vy / speed;
    const px = -ty;
    const py = tx;
    const scale = 1 + (growthPer1k * arcLength) / 1000;
    const phase = (2 * Math.PI * arcLength) / wavelength;
    const s = Math.sin(phase);
    const c = Math.cos(phase);
    const mag = amplitude * scale;
    const ox = mag * (s * px + c * tx);
    const oy = mag * (s * py + c * ty);
    return {
        x: nx + ox,
        y: ny + oy,
        arcLength,
    };
}

/** Value for SVG `stroke-dasharray`, or `undefined` for a solid stroke. */
export function strokeDashArrayFromSettings(
    dashed: boolean,
    dashLength: number,
    gapLength: number
): string | undefined {
    if (!dashed || dashLength <= 0 || gapLength <= 0) return undefined;
    return `${dashLength} ${gapLength}`;
}

/**
 * Unit vector perpendicular to the nominal stroke direction (to the left of motion).
 * When `dir` is missing or degenerate, uses normalized `(fallbackVx, fallbackVy)`, or `(1, 0)` if those are zero.
 */
export function unitPerpendicularToMotion(
    dir: { dx: number; dy: number } | null | undefined,
    fallbackVx: number,
    fallbackVy: number
): { nx: number; ny: number } {
    let ux: number;
    let uy: number;
    if (dir) {
        const dlen = Math.hypot(dir.dx, dir.dy);
        if (dlen >= 1e-9) {
            ux = dir.dx / dlen;
            uy = dir.dy / dlen;
        } else {
            const flen = Math.hypot(fallbackVx, fallbackVy);
            if (flen >= 1e-9) {
                ux = fallbackVx / flen;
                uy = fallbackVy / flen;
            } else {
                ux = 1;
                uy = 0;
            }
        }
    } else {
        const flen = Math.hypot(fallbackVx, fallbackVy);
        if (flen >= 1e-9) {
            ux = fallbackVx / flen;
            uy = fallbackVy / flen;
        } else {
            ux = 1;
            uy = 0;
        }
    }
    return { nx: -uy, ny: ux };
}

function escapeSvgAttr(d: string): string {
    return d.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

export type AgentBrushMode =
    | "stroke"
    | "circles"
    | "polygon"
    | "text"
    | "textWithStroke";

/** Use a deterministic font for export/import parity across tools (incl. Figma). */
export const TEXT_BRUSH_FONT_FAMILY = "Arial";

/** Stamp brushes: fixed frame interval vs. distance along the path (SVG px). */
export type BrushStampSpacingMode = "frames" | "distance";

/**
 * Distance-based stamping along the straight segment from A to B (one simulation step).
 * `carry` is distance already traveled since the last stamp.
 */
export function stampPositionsAlongSegment(
    spacingPx: number,
    carry: number,
    ax: number,
    ay: number,
    bx: number,
    by: number,
): { positions: { x: number; y: number }[]; carryNext: number } {
    const D = Math.max(1e-6, spacingPx);
    let r = Math.max(0, carry);
    const vx = bx - ax;
    const vy = by - ay;
    const L = Math.hypot(vx, vy);
    if (L < 1e-12) {
        return { positions: [], carryNext: r };
    }
    const ux = vx / L;
    const uy = vy / L;
    const positions: { x: number; y: number }[] = [];
    let pos = 0;
    while (pos < L - 1e-9) {
        const need = D - r;
        if (pos + need <= L + 1e-9) {
            pos += need;
            positions.push({
                x: ax + ux * pos,
                y: ay + uy * pos,
            });
            r = 0;
        } else {
            r += L - pos;
            pos = L;
        }
    }
    return { positions, carryNext: r };
}

export interface CircleStamp {
    kind: "circle";
    cx: number;
    cy: number;
    r: number;
}

export interface PolygonStamp {
    kind: "polygon";
    points: { x: number; y: number }[];
}

export interface TextStamp {
    kind: "text";
    x: number;
    y: number;
    /** Counterclockwise degrees; 0 = +x (tangent to velocity). */
    rotationDeg: number;
    char: string;
    fontSize: number;
}

export type AgentStamp = CircleStamp | PolygonStamp | TextStamp;

const RANDOM_BRUSH_TEXT_CHARS =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789 .,!?-";

/** When the text field is empty, each new agent gets a fresh random string to cycle through. */
export function generateRandomBrushText(minLen = 24, maxLen = 56): string {
    const lo = Math.min(minLen, maxLen);
    const hi = Math.max(minLen, maxLen);
    const len = lo + Math.floor(Math.random() * (hi - lo + 1));
    let s = "";
    for (let i = 0; i < len; i++) {
        s +=
            RANDOM_BRUSH_TEXT_CHARS[
                Math.floor(Math.random() * RANDOM_BRUSH_TEXT_CHARS.length)
            ]!;
    }
    return s;
}

export function resolveBrushTextInput(userInput: string): string {
    const t = userInput.trim();
    if (t.length > 0) return t;
    return generateRandomBrushText();
}

export function pickNextBrushTextChar(
    source: string,
    index: number,
): { char: string; nextIndex: number } {
    if (source.length === 0) return { char: "?", nextIndex: 0 };
    const char = source[index % source.length]!;
    return { char, nextIndex: index + 1 };
}

function escapeSvgTextContent(s: string): string {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

/** Regular n-gon centered at (cx, cy); first vertex at top (-90°). */
export function regularPolygonPoints(
    cx: number,
    cy: number,
    r: number,
    n: number
): { x: number; y: number }[] {
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
    }
    return pts;
}

export function createBrushStamp(
    mode: "circles" | "polygon",
    cx: number,
    cy: number,
    liveStrokeWidth: number,
    polygonSides: number,
    sizeScale: number
): AgentStamp {
    const r = Math.max(0.5, liveStrokeWidth * sizeScale);
    if (mode === "circles") {
        return { kind: "circle", cx, cy, r };
    }
    const sides = Math.max(3, Math.min(24, Math.round(polygonSides)));
    return {
        kind: "polygon",
        points: regularPolygonPoints(cx, cy, r, sides),
    };
}

/** One letter/symbol sample; aligned to velocity (vx, vy). Font size tracks stroke × scale (like stamp radius). */
export function createTextStamp(
    cx: number,
    cy: number,
    liveStrokeWidth: number,
    sizeScale: number,
    char: string,
    vx: number,
    vy: number
): TextStamp {
    const ch =
        char.length > 0 ? [...char][0]! : "?";
    const speed = Math.hypot(vx, vy);
    const rotationDeg =
        speed > 1e-9
            ? (Math.atan2(vy, vx) * 180) / Math.PI
            : 0;
    const fontSize = Math.max(
        4,
        Math.min(480, liveStrokeWidth * sizeScale * 2),
    );
    return {
        kind: "text",
        x: cx,
        y: cy,
        rotationDeg,
        char: ch,
        fontSize,
    };
}

function pointsToPolygonPointsAttr(
    points: ReadonlyArray<{ x: number; y: number }>
): string {
    return points.map((p) => `${p.x},${p.y}`).join(" ");
}

/** SVG elements for stamped shapes (outline only); text uses fill with stroke color. */
export function stampsToSvgFragments(
    stamps: ReadonlyArray<AgentStamp>,
    strokeColor: string,
    outlineWidth = 1.25
): string[] {
    const out: string[] = [];
    for (const s of stamps) {
        if (s.kind === "circle") {
            out.push(
                `<circle cx="${s.cx}" cy="${s.cy}" r="${s.r}" fill="none" stroke="${strokeColor}" stroke-width="${outlineWidth}" stroke-linecap="round" />`
            );
        } else if (s.kind === "polygon") {
            const pts = pointsToPolygonPointsAttr(s.points);
            out.push(
                `<polygon points="${pts}" fill="none" stroke="${strokeColor}" stroke-width="${outlineWidth}" stroke-linejoin="round" />`
            );
        } else {
            const inner = escapeSvgTextContent(s.char);
            const fs = s.fontSize.toFixed(4);
            out.push(
                `<text x="0" y="0" transform="translate(${s.x},${s.y}) rotate(${s.rotationDeg.toFixed(4)})" text-anchor="middle" dominant-baseline="middle" alignment-baseline="middle" fill="${strokeColor}" font-size="${fs}" font-family="${TEXT_BRUSH_FONT_FAMILY}">${inner}</text>`,
            );
        }
    }
    return out;
}

/**
 * SVG fragments for a trail: one path when width is uniform, else one line per segment.
 * When `mergeVaryingWidthToSinglePath` is true and width varies, uses one path with mean width.
 * `strokeDashArray` maps to `stroke-dasharray` (omit for solid stroke).
 * When `filledVariableWidthStroke` is true, builds a filled outline polygon (ignores smooth/dash).
 * `trailTaperStartLength` / `trailTaperEndLength`: fade width over this arc length at each tip (user units).
 */
export function trailToSvgFragments(
    points: ReadonlyArray<{ x: number; y: number }>,
    strokeWidths: ReadonlyArray<number> | undefined,
    smoothPaths: boolean,
    strokeColor: string,
    fallbackStrokeWidth: number,
    mergeVaryingWidthToSinglePath = false,
    strokeDashArray?: string,
    filledVariableWidthStroke = false,
    trailTaperStartLength = 0,
    trailTaperEndLength = 0
): string[] {
    const n = points.length;
    if (n < 2) return [];

    const swFull = effectiveTrailStrokeWidths(
        points,
        strokeWidths,
        fallbackStrokeWidth,
        trailTaperStartLength,
        trailTaperEndLength,
    );
    const sw =
        swFull.length === n ? (swFull as number[]) : undefined;

    if (filledVariableWidthStroke) {
        const halfs: number[] = sw
            ? sw.map((w) => Math.max(1e-6, w) / 2)
            : Array.from({ length: n }, () => Math.max(1e-6, fallbackStrokeWidth) / 2);
        const d = polylineStrokeOutlineToPathD(points, halfs);
        if (!d) return [];
        return [
            `<path d="${escapeSvgAttr(d)}" fill="${strokeColor}" fill-rule="nonzero" stroke="none" />`,
        ];
    }
    const varying = trailUsesVaryingWidth(sw, n);
    const taperActive =
        trailTaperStartLength > 0 || trailTaperEndLength > 0;
    const useManyLineSegments =
        varying && (!mergeVaryingWidthToSinglePath || taperActive);

    if (!useManyLineSegments) {
        let w: number;
        if (varying && mergeVaryingWidthToSinglePath && sw && !taperActive) {
            w = meanStrokeWidth(sw);
        } else if (sw && sw.length === n) {
            w = sw[0];
        } else {
            w = fallbackStrokeWidth;
        }
        const d = pointsToPathDWithStyle(points, smoothPaths);
        if (!d) return [];
        const dashAttr = strokeDashArray
            ? ` stroke-dasharray="${escapeSvgAttr(strokeDashArray)}"`
            : "";
        return [
            `<path d="${escapeSvgAttr(d)}" fill="none" stroke="${strokeColor}" stroke-width="${w}" stroke-linecap="round"${dashAttr} />`,
        ];
    }

    const dashAttr = strokeDashArray
        ? ` stroke-dasharray="${escapeSvgAttr(strokeDashArray)}"`
        : "";
    const out: string[] = [];
    for (let i = 0; i < n - 1; i++) {
        const w = ((sw![i] + sw![i + 1]) / 2).toFixed(4);
        const p1 = points[i];
        const p2 = points[i + 1];
        out.push(
            `<line x1="${p1.x}" y1="${p1.y}" x2="${p2.x}" y2="${p2.y}" stroke="${strokeColor}" stroke-width="${w}" stroke-linecap="round"${dashAttr} />`
        );
    }
    return out;
}

/** Split on newlines, commas, or semicolons; trim entries. Empty string → `[]` (no fallback). */
export function parsePaletteTextToColors(text: string): string[] {
    return text
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
}

export function colorsToPaletteText(colors: string[]): string {
    return colors.join("\n");
}

/** Split on newlines, commas, or semicolons; trim entries. Empty input uses `fallback` only. */
export function parseStrokePaletteInput(
    text: string,
    fallback: string
): string[] {
    const raw = parsePaletteTextToColors(text);
    if (raw.length === 0) return [fallback];
    return raw;
}

export function pickStrokeColorFromPalette(palette: string[]): string {
    const p = palette.length > 0 ? palette : ["#888888"];
    return p[Math.floor(Math.random() * p.length)]!;
}

/**
 * When `randomPerAgent` is false, returns the primary stroke color only.
 * Otherwise picks uniformly from the palette (parsed from `paletteText`, or `[primaryStrokeColor]` when empty).
 */
export function resolveAgentStrokeColor(options: {
    randomPerAgent: boolean;
    paletteText: string;
    primaryStrokeColor: string;
}): string {
    if (!options.randomPerAgent) return options.primaryStrokeColor;
    const palette = parseStrokePaletteInput(
        options.paletteText,
        options.primaryStrokeColor
    );
    return pickStrokeColorFromPalette(palette);
}
